import { ErrorBoundary, Provider } from '@rollbar/react'
import { Toast } from '@systemeio/ui'
import { GrowthbookClientProvider } from 'growthbook/client'
import { NextPage } from 'next'
import { SSRConfig, appWithTranslation } from 'next-i18next'
import NextApp from 'next/app'
import type { AppContext, AppInitialProps, AppProps } from 'next/app'
import { Router } from 'next/router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { ReactElement, ReactNode, useEffect } from 'react'
import { rollbar } from 'rollbar/instance'
import { avertaFont, FontVariableEnum, interFont } from 'shared/constants/fonts'
import DashboardLayout from 'shared/layout/components/dashboard-layout'
import fixFailedToExecuteRemoveChildOnNode from 'shared/utils/fix-failed-to-execute-remove-child-on-node'
import { SWRConfig } from 'swr'
import '../../styles/globals.css'
import {
  META_DATA_FROM_SERVER_KEY,
  MetaDataFromServerInterface,
} from 'shared/hooks/use-metadata-from-server'

export type NextPageWithLayout<P = {}, R = P> = NextPage<PageProps> & {
  getLayout: (page: ReactElement, browserTabTitle: PageProps['browserTabTitle']) => ReactNode
  getSecondaryLayout: (page: ReactElement<P>) => ReactElement<R>
}

export interface PageProps {
  browserTabTitle: string
}

type AppPropsWithLayout = AppProps<PageProps> &
  MetaDataFromServerInterface & {
    pageProps: SSRConfig
  } & {
    Component: NextPageWithLayout
  }

fixFailedToExecuteRemoveChildOnNode()

const getDefaultPageLayout = (
  page: ReactElement,
  browserTabTitle: PageProps['browserTabTitle'],
) => <DashboardLayout browserTabTitle={browserTabTitle}>{page}</DashboardLayout>

function App({ Component, pageProps, hostOnServer }: AppPropsWithLayout) {
  useEffect(() => {
    const handleRouteStart = () => NProgress.start()
    const handleRouteDone = () => NProgress.done()

    Router.events.on('routeChangeStart', handleRouteStart)
    Router.events.on('routeChangeComplete', handleRouteDone)
    Router.events.on('routeChangeError', handleRouteDone)

    return () => {
      // Make sure to remove the event handler on unmount!
      Router.events.off('routeChangeStart', handleRouteStart)
      Router.events.off('routeChangeComplete', handleRouteDone)
      Router.events.off('routeChangeError', handleRouteDone)
    }
  }, [])

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? getDefaultPageLayout
  const getSecondaryLayout = Component.getSecondaryLayout ?? (page => page)

  return (
    <>
      <style jsx global>{`
        :root {
          ${FontVariableEnum.Inter}: ${interFont.style.fontFamily};
          ${FontVariableEnum.Averta}: ${avertaFont.style.fontFamily};
        }
      `}</style>
      {/*<Script*/}
      {/*  strategy="beforeInteractive"*/}
      {/*  crossOrigin="anonymous"*/}
      {/*  src="https://unpkg.com/date-time-format-timezone@latest/build/browserified/date-time-format-timezone-complete-min.js"*/}
      {/*/>*/}
      <Provider instance={rollbar}>
        <ErrorBoundary>
          <SWRConfig
            value={{
              revalidateOnFocus: false,
              revalidateIfStale: false,
              onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
                if (retryCount > 3) {
                  if (error.status === 502 || error.status === 504) return
                }
              },
              fallback: {
                [META_DATA_FROM_SERVER_KEY]: { hostOnServer },
              },
            }}
          >
            <GrowthbookClientProvider>
              {getLayout(
                getSecondaryLayout(<Component {...pageProps} />),
                pageProps.browserTabTitle,
              )}
              <Toast />
            </GrowthbookClientProvider>
          </SWRConfig>
        </ErrorBoundary>
      </Provider>
    </>
  )
}

App.getInitialProps = async (
  context: AppContext,
): Promise<MetaDataFromServerInterface & AppInitialProps> => {
  const ctx = await NextApp.getInitialProps(context)

  const hostOnServer = context.ctx.req?.headers?.host || null

  return { ...ctx, hostOnServer }
}

export default appWithTranslation(App)
